import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import CloseRounded from "@mui/icons-material/CloseRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { StoreRecommendationState } from "context/StoreRecommendationContext";
import MapControls from "./MapControls/MapControls";
import MapView from "./MapView/MapView";
import { DEFAULT_FILTER_OBJ, LAYER_OPTIONS, STATE_OPTIONS, SUMMARY_CARDS } from "./MapControls/data";

const EMPTY_FEATURE_COLLECTION = {
	type: "FeatureCollection",
	features: [],
};

const EMPTY_LIST = [];
const NONE_BRAND_SENTINEL = "__NONE__";

const STORE_MARKER_OFFSETS = {
	ebo: [0.00012, -0.00012],
	mbo: [0, 0.00016],
	lfs: [-0.00012, -0.00012],
};

const normalizeFeatureCollection = (payload) => {
	const candidates = [payload?.feature_collection, payload?.geojson, payload?.data, payload];

	for (const candidate of candidates) {
		if (candidate?.type === "FeatureCollection" && Array.isArray(candidate.features)) {
			return candidate;
		}
	}

	return EMPTY_FEATURE_COLLECTION;
};

const normalizeResultArray = (payload) => {
	const candidates = [payload?.result, payload?.data, payload];
	for (const candidate of candidates) {
		if (Array.isArray(candidate)) {
			return candidate;
		}
	}
	return EMPTY_LIST;
};

const getSortedStates = (states = []) => [...states].sort((left, right) => left.localeCompare(right));

const buildStateKey = (states = []) => {
	const sortedStates = getSortedStates(states);
	return sortedStates.length ? sortedStates.join("|") : "ALL_INDIA";
};

const buildSkaterCacheKey = ({ states = [], regionSubset = "all" }) =>
	JSON.stringify({
		states: getSortedStates(states),
		regionSubset,
	});

const cloneFilterObj = (filterObj) => ({
	...filterObj,
	layers: { ...filterObj.layers },
	selectedStates: [...filterObj.selectedStates],
	selectedCompetitorBrands: [...(filterObj.selectedCompetitorBrands ?? [])],
});

const toNumber = (value) => {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value, digits = 2) => {
	const parsed = toNumber(value);
	return parsed == null ? "-" : parsed.toFixed(digits);
};

const normalizeScore = (value, minValue, maxValue) => {
	const parsed = toNumber(value);
	if (parsed == null) return 0;
	if (minValue == null || maxValue == null || minValue === maxValue) return 0.5;
	return Math.max(0, Math.min(1, (parsed - minValue) / (maxValue - minValue)));
};

const hashStringToColor = (value = "") => {
	let hash = 0;
	for (let index = 0; index < value.length; index += 1) {
		hash = value.charCodeAt(index) + ((hash << 5) - hash);
	}

	const hue = Math.abs(hash) % 360;
	return `hsl(${hue} 58% 56%)`;
};

const annotateSkaterFeatureCollection = (featureCollection) => {
	if (!featureCollection?.features?.length) {
		return EMPTY_FEATURE_COLLECTION;
	}

	const regionScores = featureCollection.features
		.map((feature) => toNumber(feature?.properties?.region_score))
		.filter((value) => value != null);
	const priorityScores = featureCollection.features
		.map((feature) => toNumber(feature?.properties?.priority_score))
		.filter((value) => value != null);

	const minRegionScore = regionScores.length ? Math.min(...regionScores) : null;
	const maxRegionScore = regionScores.length ? Math.max(...regionScores) : null;
	const minPriorityScore = priorityScores.length ? Math.min(...priorityScores) : null;
	const maxPriorityScore = priorityScores.length ? Math.max(...priorityScores) : null;

	return {
		...featureCollection,
		features: featureCollection.features.map((feature) => {
			const properties = feature?.properties ?? {};
			const regionId = properties.region_id || "UNASSIGNED";
			const regionScore = toNumber(properties.region_score);
			const priorityScore = toNumber(properties.priority_score);

			return {
				...feature,
				properties: {
					...properties,
					cluster_color: properties.cluster_color ?? hashStringToColor(regionId),
					hue_score_norm: normalizeScore(regionScore, minRegionScore, maxRegionScore),
					priority_score_norm: normalizeScore(priorityScore, minPriorityScore, maxPriorityScore),
					region_score_display: properties.region_score_display ?? formatNumber(regionScore),
					priority_score_display: properties.priority_score_display ?? formatNumber(priorityScore),
					score_display: properties.score_display ?? formatNumber(properties.smoothed_score),
				},
			};
		}),
	};
};

const buildStorePointFeatureCollection = (points = []) => ({
	type: "FeatureCollection",
	features: points.flatMap((point, index) => {
		const baseLatitude = toNumber(point.plot_latitude ?? point.latitude);
		const baseLongitude = toNumber(point.plot_longitude ?? point.longitude);
		if (baseLatitude == null || baseLongitude == null) {
			return [];
		}

		return [
			{ key: "ebo", enabled: Boolean(point.has_ebo), label: "E", color: "#f4b400", revenueKey: "ebo_revenue" },
			{ key: "mbo", enabled: Boolean(point.has_mbo), label: "M", color: "#198754", revenueKey: "mbo_revenue" },
			{ key: "lfs", enabled: Boolean(point.has_lfs), label: "L", color: "#7c3aed", revenueKey: "lfs_revenue" },
		]
			.filter((definition) => definition.enabled)
			.map((definition) => {
				const offset = STORE_MARKER_OFFSETS[definition.key] ?? [0, 0];
				return {
					type: "Feature",
					id: `${definition.key}-${point.pincode ?? index}`,
					properties: {
						...point,
						layer_key: `STORE_${definition.key.toUpperCase()}_POINTS`,
						store_kind: definition.key,
						marker_label: definition.label,
						render_color: definition.color,
						store_revenue: point[definition.revenueKey] ?? 0,
					},
					geometry: {
						type: "Point",
						coordinates: [baseLongitude + offset[1], baseLatitude + offset[0]],
					},
				};
			});
	}),
});

const buildCompetitorPointFeatureCollection = (points = []) => ({
	type: "FeatureCollection",
	features: points
		.map((point, index) => {
			const latitude = toNumber(point.lat);
			const longitude = toNumber(point.lng);
			if (latitude == null || longitude == null) {
				return null;
			}

			return {
				type: "Feature",
				id: point.place_id ?? `competitor-${index}`,
				properties: {
					...point,
					layer_key: "COMPETITOR_POINTS",
					render_color: hashStringToColor(point.brand_display || point.brand_key || "competitor"),
				},
				geometry: {
					type: "Point",
					coordinates: [longitude, latitude],
				},
			};
		})
		.filter(Boolean),
});

const filterPointsByCompetitorBrands = (points, selectedCompetitorBrands) => {
	if (selectedCompetitorBrands?.includes(NONE_BRAND_SENTINEL)) {
		return EMPTY_LIST;
	}

	if (!selectedCompetitorBrands?.length) {
		return points;
	}

	const selectedBrandSet = new Set(selectedCompetitorBrands);
	return points.filter((point) => selectedBrandSet.has(point.brand_display));
};

const getCompetitorBrandOptions = (points) =>
	Array.from(new Set(points.map((point) => point.brand_display).filter(Boolean))).sort((left, right) => left.localeCompare(right));

const Home = () => {
	const { mapDataCache, setMapDataCache } = StoreRecommendationState();
	const [stateOptions, setStateOptions] = useState(STATE_OPTIONS);
	const [filterObj, setFilterObj] = useState(cloneFilterObj(DEFAULT_FILTER_OBJ));
	const [appliedFilterObj, setAppliedFilterObj] = useState(cloneFilterObj(DEFAULT_FILTER_OBJ));
	const [controlsOpen, setControlsOpen] = useState(true);
	const [masterPolygonData, setMasterPolygonData] = useState(EMPTY_FEATURE_COLLECTION);
	const [onlinePolygonData, setOnlinePolygonData] = useState(EMPTY_FEATURE_COLLECTION);
	const [skaterPolygonData, setSkaterPolygonData] = useState(EMPTY_FEATURE_COLLECTION);
	const [storePointData, setStorePointData] = useState(EMPTY_LIST);
	const [competitorPointData, setCompetitorPointData] = useState(EMPTY_LIST);
	const [worldStatus, setWorldStatus] = useState("loading");
	const [masterStatus, setMasterStatus] = useState("idle");
	const [onlineStatus, setOnlineStatus] = useState("idle");
	const [skaterStatus, setSkaterStatus] = useState("idle");
	const [storeStatus, setStoreStatus] = useState("idle");
	const [competitorStatus, setCompetitorStatus] = useState("idle");
	const [isApplying, setIsApplying] = useState(false);
	const [mapRendered, setMapRendered] = useState(false);

	const selectedStatesForApi = useMemo(
		() => (appliedFilterObj.selectedStates.includes("All India") ? [] : appliedFilterObj.selectedStates.filter(Boolean)),
		[appliedFilterObj.selectedStates],
	);

	const selectedStateCacheKey = useMemo(() => buildStateKey(selectedStatesForApi), [selectedStatesForApi]);
	const skaterCacheKey = useMemo(
		() =>
			buildSkaterCacheKey({
				states: selectedStatesForApi,
				regionSubset: appliedFilterObj.regionSubset,
			}),
		[selectedStatesForApi, appliedFilterObj.regionSubset],
	);

	const storePointFeatureCollection = useMemo(() => buildStorePointFeatureCollection(storePointData), [storePointData]);
	const filteredCompetitorPoints = useMemo(
		() => filterPointsByCompetitorBrands(competitorPointData, appliedFilterObj.selectedCompetitorBrands),
		[appliedFilterObj.selectedCompetitorBrands, competitorPointData],
	);
	const competitorPointFeatureCollection = useMemo(
		() => buildCompetitorPointFeatureCollection(filteredCompetitorPoints),
		[filteredCompetitorPoints],
	);
	const competitorBrandOptions = useMemo(() => getCompetitorBrandOptions(competitorPointData), [competitorPointData]);

	const hasPendingChanges = useMemo(
		() => JSON.stringify(filterObj) !== JSON.stringify(appliedFilterObj),
		[appliedFilterObj, filterObj],
	);

	const activeLoadingStates = useMemo(
		() => [masterStatus, onlineStatus, skaterStatus, storeStatus, competitorStatus],
		[competitorStatus, masterStatus, onlineStatus, skaterStatus, storeStatus],
	);

	useEffect(() => {
		setFilterObj((prev) => {
			if (!prev.selectedCompetitorBrands.length) {
				return prev;
			}

			const nextSelectedBrands = prev.selectedCompetitorBrands.filter(
				(brand) => brand === NONE_BRAND_SENTINEL || competitorBrandOptions.includes(brand),
			);
			if (nextSelectedBrands.length === prev.selectedCompetitorBrands.length) {
				return prev;
			}

			return {
				...prev,
				selectedCompetitorBrands: nextSelectedBrands,
			};
		});
	}, [competitorBrandOptions]);

	const handleApplyFilters = (nextFilterObj = filterObj) => {
		setAppliedFilterObj(cloneFilterObj(nextFilterObj));
		setMapRendered(false);
	};

	useEffect(() => {
		const features = masterPolygonData?.features ?? [];
		if (!features.length) return;
		const states = [...new Set(features.map((f) => f?.properties?.state || f?.properties?.State).filter(Boolean))].sort((a, b) =>
			a.localeCompare(b),
		);
		if (states.length) setStateOptions(["All India", ...states]);
	}, [masterPolygonData]);

	useEffect(() => {
		const requiresMaster = appliedFilterObj.layers.MASTER_POLYGONS || appliedFilterObj.layers.STATE_BORDERS;
		if (!requiresMaster) {
			setMasterPolygonData(EMPTY_FEATURE_COLLECTION);
			setMasterStatus("idle");
			return;
		}

		const cachedData = mapDataCache.masterPolygonsByStateKey[selectedStateCacheKey];
		if (cachedData) {
			setMasterPolygonData(cachedData);
			setMasterStatus("ready");
			return;
		}

		let isActive = true;
		setMasterStatus("loading");

		axios
			.post("/api/store_recommendation/map/master-polygons", { states: selectedStatesForApi })
			.then((response) => {
				if (!isActive) return;
				const featureCollection = normalizeFeatureCollection(response.data);
				setMasterPolygonData(featureCollection);
				setMasterStatus("ready");
				setMapDataCache((prev) => ({
					...prev,
					masterPolygonsByStateKey: {
						...prev.masterPolygonsByStateKey,
						[selectedStateCacheKey]: featureCollection,
					},
				}));
			})
			.catch(() => {
				if (!isActive) return;
				setMasterPolygonData(EMPTY_FEATURE_COLLECTION);
				setMasterStatus("error");
			});

		return () => {
			isActive = false;
		};
	}, [
		appliedFilterObj.layers.MASTER_POLYGONS,
		appliedFilterObj.layers.STATE_BORDERS,
		mapDataCache.masterPolygonsByStateKey,
		selectedStateCacheKey,
		selectedStatesForApi,
		setMapDataCache,
	]);

	useEffect(() => {
		if (!appliedFilterObj.layers.ONLINE_POLYGONS) {
			setOnlinePolygonData(EMPTY_FEATURE_COLLECTION);
			setOnlineStatus("idle");
			return;
		}

		const cachedData = mapDataCache.onlinePolygonsByStateKey[selectedStateCacheKey];
		if (cachedData) {
			setOnlinePolygonData(cachedData);
			setOnlineStatus("ready");
			return;
		}

		let isActive = true;
		setOnlineStatus("loading");

		axios
			.post("/api/store_recommendation/map/online-polygons", { states: selectedStatesForApi })
			.then((response) => {
				if (!isActive) return;
				const featureCollection = normalizeFeatureCollection(response.data);
				setOnlinePolygonData(featureCollection);
				setOnlineStatus("ready");
				setMapDataCache((prev) => ({
					...prev,
					onlinePolygonsByStateKey: {
						...prev.onlinePolygonsByStateKey,
						[selectedStateCacheKey]: featureCollection,
					},
				}));
			})
			.catch(() => {
				if (!isActive) return;
				setOnlinePolygonData(EMPTY_FEATURE_COLLECTION);
				setOnlineStatus("error");
			});

		return () => {
			isActive = false;
		};
	}, [
		appliedFilterObj.layers.ONLINE_POLYGONS,
		mapDataCache.onlinePolygonsByStateKey,
		selectedStateCacheKey,
		selectedStatesForApi,
		setMapDataCache,
	]);

	useEffect(() => {
		if (!appliedFilterObj.layers.SKATER_POLYGONS) {
			setSkaterPolygonData(EMPTY_FEATURE_COLLECTION);
			setSkaterStatus("idle");
			return;
		}

		const cachedData = mapDataCache.skaterPolygonsByFilterKey[skaterCacheKey];
		if (cachedData) {
			setSkaterPolygonData(cachedData);
			setSkaterStatus("ready");
			return;
		}

		let isActive = true;
		setSkaterStatus("loading");

		axios
			.post("/api/store_recommendation/map/skater-polygons", {
				states: selectedStatesForApi,
				region_subset: appliedFilterObj.regionSubset,
			})
			.then((response) => {
				if (!isActive) return;
				const featureCollection = annotateSkaterFeatureCollection(normalizeFeatureCollection(response.data));
				setSkaterPolygonData(featureCollection);
				setSkaterStatus("ready");
				setMapDataCache((prev) => ({
					...prev,
					skaterPolygonsByFilterKey: {
						...prev.skaterPolygonsByFilterKey,
						[skaterCacheKey]: featureCollection,
					},
				}));
			})
			.catch(() => {
				if (!isActive) return;
				setSkaterPolygonData(EMPTY_FEATURE_COLLECTION);
				setSkaterStatus("error");
			});

		return () => {
			isActive = false;
		};
	}, [
		appliedFilterObj.layers.SKATER_POLYGONS,
		appliedFilterObj.regionSubset,
		mapDataCache.skaterPolygonsByFilterKey,
		selectedStatesForApi,
		setMapDataCache,
		skaterCacheKey,
	]);

	useEffect(() => {
		const needsStorePoints =
			appliedFilterObj.layers.STORE_EBO_POINTS ||
			appliedFilterObj.layers.STORE_MBO_POINTS ||
			appliedFilterObj.layers.STORE_LFS_POINTS;
		if (!needsStorePoints) {
			setStorePointData(EMPTY_LIST);
			setStoreStatus("idle");
			return;
		}

		const cachedData = mapDataCache.storePointsByStateKey[selectedStateCacheKey];
		if (cachedData) {
			setStorePointData(cachedData);
			setStoreStatus("ready");
			return;
		}

		let isActive = true;
		setStoreStatus("loading");

		axios
			.get("/api/store_recommendation/store-points", {
				params: { states: selectedStatesForApi.join(","), storeType: "all" },
			})
			.then((response) => {
				if (!isActive) return;
				const result = normalizeResultArray(response.data);
				setStorePointData(result);
				setStoreStatus("ready");
				setMapDataCache((prev) => ({
					...prev,
					storePointsByStateKey: {
						...prev.storePointsByStateKey,
						[selectedStateCacheKey]: result,
					},
				}));
			})
			.catch(() => {
				if (!isActive) return;
				setStorePointData(EMPTY_LIST);
				setStoreStatus("error");
			});

		return () => {
			isActive = false;
		};
	}, [
		appliedFilterObj.layers.STORE_EBO_POINTS,
		appliedFilterObj.layers.STORE_LFS_POINTS,
		appliedFilterObj.layers.STORE_MBO_POINTS,
		mapDataCache.storePointsByStateKey,
		selectedStateCacheKey,
		selectedStatesForApi,
		setMapDataCache,
	]);

	useEffect(() => {
		if (!appliedFilterObj.layers.COMPETITOR_POINTS) {
			setCompetitorPointData(EMPTY_LIST);
			setCompetitorStatus("idle");
			return;
		}

		const cachedData = mapDataCache.competitorPointsByStateKey[selectedStateCacheKey];
		if (cachedData) {
			setCompetitorPointData(cachedData);
			setCompetitorStatus("ready");
			return;
		}

		let isActive = true;
		setCompetitorStatus("loading");

		axios
			.get("/api/store_recommendation/competitor-points", {
				params: { states: selectedStatesForApi.join(",") },
			})
			.then((response) => {
				if (!isActive) return;
				const result = normalizeResultArray(response.data);
				setCompetitorPointData(result);
				setCompetitorStatus("ready");
				setMapDataCache((prev) => ({
					...prev,
					competitorPointsByStateKey: {
						...prev.competitorPointsByStateKey,
						[selectedStateCacheKey]: result,
					},
				}));
			})
			.catch(() => {
				if (!isActive) return;
				setCompetitorPointData(EMPTY_LIST);
				setCompetitorStatus("error");
			});

		return () => {
			isActive = false;
		};
	}, [
		appliedFilterObj.layers.COMPETITOR_POINTS,
		mapDataCache.competitorPointsByStateKey,
		selectedStateCacheKey,
		selectedStatesForApi,
		setMapDataCache,
	]);

	useEffect(() => {
		if (!isApplying) return;
		if (activeLoadingStates.includes("loading")) return;
		setIsApplying(false);
	}, [activeLoadingStates, isApplying]);

	return (
		<Box
			sx={{
				position: "relative",
				height: "calc(100vh - 118px)",
				overflow: "hidden",
				background: "#f5f6f7",
				boxSizing: "border-box",
			}}>
			<MapView
				filterObj={appliedFilterObj}
				masterPolygonData={masterPolygonData}
				onlinePolygonData={onlinePolygonData}
				skaterPolygonData={skaterPolygonData}
				storePointData={storePointFeatureCollection}
				competitorPointData={competitorPointFeatureCollection}
				worldStatus={worldStatus}
				setWorldStatus={setWorldStatus}
				masterStatus={masterStatus}
				onlineStatus={onlineStatus}
				skaterStatus={skaterStatus}
				storeStatus={storeStatus}
				competitorStatus={competitorStatus}
				selectedStatesForMap={selectedStatesForApi}
				onMapRendered={setMapRendered}
			/>

			<IconButton
				onClick={() => setControlsOpen((prev) => !prev)}
				sx={{
					position: "absolute",
					top: 16,
					left: 16,
					zIndex: 1201,
					width: 48,
					height: 48,
					border: "1px solid rgba(34,68,58,0.12)",
					background: "#17362f",
					color: "#fff",
					boxShadow: "0 16px 30px rgba(25, 48, 41, 0.24)",
					"&:hover": {
						background: "#102720",
					},
				}}>
				{controlsOpen ? <CloseRounded /> : <TuneRounded />}
			</IconButton>

			<MapControls
				filterObj={filterObj}
				setFilterObj={setFilterObj}
				controlsOpen={controlsOpen}
				stateOptions={stateOptions}
				layerOptions={LAYER_OPTIONS}
				summaryCards={SUMMARY_CARDS}
				competitorBrandOptions={competitorBrandOptions}
				onApplyFilters={handleApplyFilters}
				isApplying={isApplying}
				hasPendingChanges={hasPendingChanges}
			/>

			{(!mapRendered || worldStatus === "loading" || activeLoadingStates.includes("loading")) && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						zIndex: 1400,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(244, 247, 250, 0.54)",
						backdropFilter: "blur(5px)",
					}}>
					<Paper
						elevation={0}
						sx={{
							px: 3,
							py: 2.25,
							minWidth: 300,
							borderRadius: 4,
							background: "rgba(255,255,255,0.98)",
							border: "1px solid rgba(53,87,126,0.16)",
							boxShadow: "0 20px 38px rgba(15, 23, 42, 0.14)",
						}}>
						<Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
							<CircularProgress size={22} sx={{ color: "#35577e" }} />
							<Typography sx={{ fontSize: 14, fontWeight: 700, color: "#24435f" }}>Loading map</Typography>
						</Stack>
						<Stack spacing={0.75}>
							{[
								{ label: "Base map", status: worldStatus },
								{ label: "Master polygons", status: masterStatus },
								{ label: "Online polygons", status: onlineStatus },
								{ label: "SKATER polygons", status: skaterStatus },
								{ label: "Store points", status: storeStatus },
								{ label: "Competitor points", status: competitorStatus },
								{ label: "Render full map", status: mapRendered ? "ready" : activeLoadingStates.includes("loading") || worldStatus === "loading" ? "idle" : "loading" },
							].map(({ label, status }) => (
								<Stack key={label} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
									<Typography sx={{ fontSize: 12, color: "#546678" }}>{label}</Typography>
									<Chip
										size="small"
										label={status === "loading" ? "Loading" : status === "ready" ? "Ready" : status === "error" ? "Error" : "Idle"}
										sx={{
											fontWeight: 700,
											...(status === "loading" && { background: "#fff4d6", color: "#855f00", border: "1px solid #efd79b" }),
											...(status === "ready" && { background: "#e7f4ec", color: "#1d5b38", border: "1px solid #bad8c5" }),
											...(status === "error" && { background: "#fdeceb", color: "#a63c32", border: "1px solid #f2c2bc" }),
											...(status === "idle" && { background: "#edf1ef", color: "#54635d", border: "1px solid #d3ddda" }),
										}}
									/>
								</Stack>
							))}
						</Stack>
					</Paper>
				</Box>
			)}
		</Box>
	);
};

export default Home;
